import { useParams, useNavigate } from "react-router-dom";
import { getDeleteTemplateErrorMessage } from "shared/app/VerificationTemplate/deleteTemplateError";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  useMutation
} from "@tanstack/react-query";
import templatesApi from "api/landlord/templates";
import { getErrorMessage } from "utils/error";

import { toast } from "react-toastify";
import { Template } from "types/landlord/verificationTemplate";
import { useUnderwritingAgentsPayload } from "shared/app/VerificationTemplate/useUnderwritingAgentsPayload";
import { useSaveTemplateRulesDraft } from "shared/app/VerificationTemplate/useTemplateRulesForm";
import type { TemplateRulesDraft } from "api/underwriting-agents/types";

export function useVerificationTemplateController(templateId?: string) {
  const { id: paramsId } = useParams();
  const navigate = useNavigate();

  // If templateId is explicitly passed (even as undefined), use it
  // Only fallback to paramsId if templateId is not provided at all
  const id = arguments.length > 0 ? templateId : paramsId;

  const queryClient = useQueryClient();
  const withUnderwritingAgents = useUnderwritingAgentsPayload();
  const saveTemplateRulesDraft = useSaveTemplateRulesDraft();

  const templateQuery = useQuery({
    queryKey: ["VERIFICATION_TEMPLATE", id],
    queryFn: () => templatesApi.getTemplate(id || ""),
    placeholderData: keepPreviousData,
    enabled: Boolean(id)
  });

  const verificationSchemasQuery = useQuery({
    queryKey: ["VERIFICATION_SCHEMAS"],
    queryFn: () => templatesApi.getVerificationSchemas()
  });

  const deleteTemplate = async () => {
    try {
      await templatesApi.deleteTemplate(id || "");
      toast.success("Template has been successfully deleted");
      navigate(`/verification-templates`);
    } catch (err) {
      toast.error(getDeleteTemplateErrorMessage(err));
    }
  };

  const { mutate: updateTemplate } = useMutation({
    // The kept rule changes are saved before the template reloads, so it
    // doesn't show the agent's rules without them first.
    mutationFn: async ({
      template: updatedTemplate,
      rulesDraft
    }: {
      template: Template;
      rulesDraft?: TemplateRulesDraft;
    }) => {
      const data = await templatesApi.updateTemplate(id || "", updatedTemplate);
      await saveTemplateRulesDraft(rulesDraft, data.verification_template);
      return data;
    },
    mutationKey: ["VERIFICATION_TEMPLATE", id],
    onSuccess: (data) => {
      toast.success("Template has been updated");
      queryClient.invalidateQueries({
        queryKey: ["VERIFICATION_TEMPLATE", id]
      });
      navigate(`/verification-templates/${data.verification_template.id}`);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const createTemplate = async (newTemplate: Template) => {
    try {
      await templatesApi.createTemplate(
        await withUnderwritingAgents(newTemplate)
      );
      toast.success("Template has been duplicated");
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      toast.error(
        errorMessage === "validation_failed"
          ? "Template name is already taken"
          : errorMessage
      );
    }
  };

  const defaultTemplateQuery = useQuery({
    queryKey: ["DEFAULT_TEMPLATE"],
    queryFn: () => templatesApi.getDefaultTemplate()
  });

  return {
    id,
    templateQuery,
    deleteTemplate,
    updateTemplate,
    createTemplate,
    verificationSchemas: verificationSchemasQuery.data?.verification_schemas,
    defaultTemplate: defaultTemplateQuery.data?.verification_template
  };
}
